/**
 * Global notification-center context.
 *
 * Mounts ONE useNotificationCenter hook for the app and exposes its state to
 * every descendant. Prevents the toast/badge logic from running multiple times
 * if several components want to read unread counts.
 */
import React, { createContext, useContext } from 'react';
import { useNotificationCenter } from '../hooks/useNotificationCenter';

const NotificationCenterContext = createContext(null);

export const NotificationCenterProvider = ({ children }) => {
  const value = useNotificationCenter();
  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
    </NotificationCenterContext.Provider>
  );
};

export const useNotificationCenterContext = () => {
  const ctx = useContext(NotificationCenterContext);
  if (!ctx) {
    // Default no-op return so consumers stay safe outside the provider
    // (e.g. on the landing page, before login).
    return {
      unreadMessages: 0,
      unreadNotifications: 0,
      notifications: [],
      clearUnreadMessages: () => {},
      clearThreadUnread: () => {},
      markAllNotificationsRead: async () => {},
      mutePreferences: {},
      toggleMute: () => {},
      dnd: null,
      enableDnd: () => {},
      disableDnd: () => {},
      lastSyncOk: null,
    };
  }
  return ctx;
};

export default NotificationCenterProvider;
